/* eslint-disable prefer-destructuring */
import { LOCATION_CHANGE } from 'connected-react-router';
import { matchPath } from 'react-router';
import { combineEpics, ofType } from 'redux-observable';
import { from, timer, race } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  distinctUntilChanged,
  tap,
  share,
} from 'rxjs/operators';

import {
  subscribeSpot,
  subscribeTicker,
  unsubscribeSpot,
  unsubscribeTicker,
} from 'actions/socket';
import {
  getAllTickers,
  getTopFuturesMarketsData,
  initiateTicker,
  killTickerTimer,
} from 'actions/trade';
import { TOGGLE_MOBILE_HEADER_DROPDOWN } from 'actionTypes/other';
import { SOCKET_CONNECTED } from 'actionTypes/socket';
import { TRADE_CONSTANTS } from 'actionTypes/trade';
import { ASSETS_SYMBOL_TO_RENDER, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { TIME_FORMATS } from 'constants/timeformats';
import { dateFormat, toUTC, getDateTime } from 'helpers/day';
import { socketActiveSelector } from 'selectors/socketSelectors';

const tickerIntializerEpic = action$ =>
  action$.pipe(
    ofType(TRADE_CONSTANTS.INITIATE_TICKER),
    switchMap(action => {
      const { contractType, frequencyTime, initiateOnCall = true } = action.payload;
      const initalTimer = initiateOnCall ? 0 : frequencyTime;
      return timer(initalTimer, frequencyTime).pipe(
        takeUntil(action$.ofType(TRADE_CONSTANTS.KILL_TICKER_TIMER)),
        map(() => {
          const apiContractType = (() => {
            switch (contractType) {
              case 'futures':
                return 'futures,perpetual_futures';
              case 'options':
              case 'options_chain':
                return 'put_options,call_options';
              case 'futuresAndOptions':
                return 'futures,perpetual_futures,put_options,call_options,spot,tracker';
              case 'spreads':
                return 'interest_rate_swaps,spreads';
              case 'allNonOptions':
                return 'futures,perpetual_futures,spot,tracker,move_options,options_combos,interest_rate_swaps,spreads';
              case 'optionsChainAndMoveOptions':
                return 'put_options,call_options,move_options';
              case 'all':
              default:
                return 'futures,perpetual_futures,spot,tracker,move_options,options_combos,put_options,call_options,interest_rate_swaps,spreads';
            }
          })();
          return getAllTickers(apiContractType);
        })
      );
    })
  );

const mobileTickerCalls = params => {
  const { pageName, contractType, productSymbolOrExpiryDate, assetSymbol } = params;
  const homePageSymbols = ASSETS_SYMBOL_TO_RENDER.map(
    asset => `${asset}${VANILLA_SETTLING_ASSET}`
  );
  if (contractType === 'options_analytics') {
    return [
      initiateTicker({
        contractType: 'optionsChainAndMoveOptions',
        frequencyTime: 10 * 1000,
        initiateOnCall: false,
      }),
      unsubscribeTicker(),
      unsubscribeSpot(''),
    ];
  }

  if (contractType === 'home') {
    return [
      killTickerTimer(),
      unsubscribeTicker(),
      subscribeSpot(),
      subscribeTicker(homePageSymbols),
    ];
  }

  // in this case assetSymbol is spotSymbol
  if (contractType === 'alerts' && assetSymbol) {
    const assetSymbolWithUnderlying = assetSymbol.replace('.DE', '');
    return [
      killTickerTimer(),
      unsubscribeTicker(),
      unsubscribeSpot(assetSymbolWithUnderlying),
    ];
  }

  switch (pageName) {
    case 'markets':
      if (contractType === 'options_chain' || contractType === 'options') {
        if (productSymbolOrExpiryDate && assetSymbol) {
          const expiryDate = dateFormat(
            toUTC(getDateTime(productSymbolOrExpiryDate)),
            TIME_FORMATS.DDMMYY
          );
          return [
            killTickerTimer(),
            unsubscribeTicker(),
            unsubscribeSpot(''),
            subscribeTicker([`${assetSymbol}-${expiryDate}`]),
          ];
        }
        return [];
      }

      if (contractType === 'futures') {
        return [
          killTickerTimer(),
          unsubscribeTicker(),
          unsubscribeSpot(''),
          subscribeTicker(['futures', 'perpetual_futures']),
        ];
      }

      return [
        // killTickerTimer(),
        // initiateTicker({
        //   contractType: 'allNonOptions',
        //   frequencyTime: 2500,
        // }),
        // pollOptionsTicker({ frequencyTime: 10000 }),
        killTickerTimer(),
        unsubscribeTicker(),
        unsubscribeSpot(''),
        subscribeTicker([`${contractType}`]),
      ];
    case 'trade':
      if (contractType === 'basketOrder') {
        // return [
        //   killTickerTimer(),
        //   initiateTicker({
        //     contractType: 'futuresAndOptions',
        //     frequencyTime: 2500,
        //   }),
        // ];
        return [killTickerTimer(), unsubscribeTicker(), unsubscribeSpot('')];
      }
      if (params.productSymbolOrExpiryDate) {
        return [
          killTickerTimer(),
          unsubscribeTicker(),
          unsubscribeSpot(params.productSymbolOrExpiryDate),
          subscribeTicker([`${params.productSymbolOrExpiryDate}`]),
        ];
      }
      return [];
    case 'balances':
      return [
        // killTickerTimer(),
        // initiateTicker({
        //   contractType: 'all',
        //   frequencyTime: 2500,
        // }),
        killTickerTimer(),
        unsubscribeTicker(),
        unsubscribeSpot(''),
      ];
    default:
      return [killTickerTimer(), unsubscribeTicker(), unsubscribeSpot('')];
  }
};

// const mobileTickerCalls = () => [];

const desktopTickerCalls = params => {
  const { pageName, contractType, productSymbolOrExpiryDate, assetSymbol } = params;

  if (contractType === 'options_analytics') {
    return [
      initiateTicker({
        contractType: 'optionsChainAndMoveOptions',
        frequencyTime: 10 * 1000,
        initiateOnCall: false,
      }),
      unsubscribeTicker(),
    ];
  }

  switch (pageName) {
    case 'balances':
      return [killTickerTimer(), unsubscribeTicker()];
    case 'markets': {
      const btcContract = `BTC${VANILLA_SETTLING_ASSET}`;
      const ethContract = `ETH${VANILLA_SETTLING_ASSET}`;

      if (contractType === 'options_chain') {
        if (productSymbolOrExpiryDate && assetSymbol) {
          const expiryDate = dateFormat(
            toUTC(getDateTime(productSymbolOrExpiryDate)),
            TIME_FORMATS.DDMMYY
          );
          return [
            killTickerTimer(),
            unsubscribeTicker(),
            subscribeTicker([`${assetSymbol}-${expiryDate}`, btcContract, ethContract]),
          ];
        }
        return [];
      }

      if (contractType === 'futures') {
        return [
          killTickerTimer(),
          unsubscribeTicker(),
          subscribeTicker([btcContract, ethContract, 'futures', 'perpetual_futures']),
        ];
      }

      return [
        killTickerTimer(),
        unsubscribeTicker(),
        subscribeTicker([btcContract, ethContract, `${contractType}`]),
      ];
    }
    case 'trade': {
      const symbol = productSymbolOrExpiryDate;
      if (!symbol) {
        return [killTickerTimer(), unsubscribeTicker()];
      }
      if (contractType === 'options_chain' || contractType === 'options') {
        const tickerSymbol = `${symbol.split('-')[1]}-${symbol.split('-')[3]}`;
        return [killTickerTimer(), unsubscribeTicker(), subscribeTicker([tickerSymbol])];
      }
      return [killTickerTimer(), unsubscribeTicker(), subscribeTicker([symbol])];
    }
    default:
      return [killTickerTimer(), unsubscribeTicker()];
  }
};

const subscribePositionTickerEpic = action$ =>
  action$.pipe(
    ofType(TRADE_CONSTANTS.POLL_ALL_OPEN_POSITION_TICKER),
    switchMap(action => {
      const { payload } = action;
      // return timer(0, 5000).pipe(
      //   map(() => get24HrTicker(commaSeparatedSymbols)),
      //   takeUntil(action$.ofType(TRADE_CONSTANTS.KILL_ALL_OPEN_POSITION_TICKER_TIMER))
      // );
      return [unsubscribeTicker(), subscribeTicker(payload)];
    })
  );

const contractDropdownTickerEpic = action$ =>
  action$.pipe(
    ofType(TOGGLE_MOBILE_HEADER_DROPDOWN),
    mergeMap(action => {
      const { payload } = action;
      return payload
        ? from([
            (killTickerTimer(),
            initiateTicker({
              contractType: 'all',
              frequencyTime: 5000,
            })),
          ])
        : from([killTickerTimer()]);
    })
  );

const pollTopGainersLosersEpic = action$ =>
  action$.pipe(
    ofType(TRADE_CONSTANTS.POLL_TOP_GAINERS_LOSERS),
    switchMap(() => {
      return timer(0, 10000).pipe(
        map(() => getTopFuturesMarketsData({ params: { sort_by: 'top_gainers' } })),
        takeUntil(action$.ofType(TRADE_CONSTANTS.KILL_TOP_GAINERS_LOSERS_TIMER))
      );
    })
  );

const getMatchedPathFromRoute = path => {
  let matchedPath = matchPath(
    path,
    '/:contractType?/:pageName?/:assetSymbol?/:productSymbolOrExpiryDate?'
  );

  if (!matchedPath) {
    matchedPath = matchPath(
      path,
      '/tradingview/:contractType/:assetSymbol/:productSymbol'
    );
  }
  return matchedPath;
};

// 2. Extract the common ticker call logic
const handleTickerCalls = (params, state) => {
  const mediaQueryList = window.matchMedia(`(max-width: 1024px)`);
  if (mediaQueryList.matches) {
    return from(mobileTickerCalls(params, state));
  }
  return from(desktopTickerCalls(params, state));
};

// 3. Create a combined epic that handles both triggers
const tickerEpic = (action$, state$) => {
  // Track the path keys that have been processed in the current session
  const processedPathsInSession = new Set();

  // Reset session when location changes
  const locationChange$ = action$.pipe(
    ofType(LOCATION_CHANGE),
    tap(() => {
      // Clear the processed paths set on each location change
      // This allows revisiting the same page to work
      processedPathsInSession.clear();
    }),
    share() // Share this stream so the clear logic runs once
  );

  // Stream for location changes
  const locationChangeTrigger$ = locationChange$.pipe(
    map(({ payload }) => payload.location.pathname),
    map(path => ({
      path,
      matchedPath: getMatchedPathFromRoute(path),
      source: 'location',
    })),
    filter(({ matchedPath }) => Boolean(matchedPath)),
    // Add a unique route key for path identification
    distinctUntilChanged((pre, curr) => pre.path === curr.path),
    map(data => {
      const params = data.matchedPath.params;
      // Create a route key that uniquely identifies this route
      const routeKey = `${params.contractType || ''}-${params.pageName || ''}-${
        params.assetSymbol || ''
      }-${params.productSymbolOrExpiryDate || ''}`;
      return { ...data, routeKey };
    }),
    // Wait for socket to be active
    switchMap(data =>
      state$.pipe(
        map(socketActiveSelector),
        filter(socketIsActive => socketIsActive),
        take(1),
        map(() => data)
      )
    )
  );

  // Stream for socket connections
  const socketConnectedTrigger$ = action$.pipe(
    ofType(SOCKET_CONNECTED),
    // Ensure we only process once per active socket connection
    map(() => socketActiveSelector(state$.value)),
    distinctUntilChanged(),
    filter(value => value === true),
    map(() => {
      const path = state$.value.router.location.pathname;
      const matchedPath = getMatchedPathFromRoute(path);
      if (!matchedPath) return { matchedPath: null };

      const params = matchedPath.params;
      const routeKey = `${params.contractType || ''}-${params.pageName || ''}-${
        params.assetSymbol || ''
      }-${params.productSymbolOrExpiryDate || ''}`;

      return {
        path,
        matchedPath,
        source: 'socket',
        routeKey,
      };
    }),
    filter(({ matchedPath }) => Boolean(matchedPath))
  );

  // Merge both streams and process
  return race(locationChangeTrigger$, socketConnectedTrigger$).pipe(
    // Only process each route once per "session" (between location changes)
    filter(data => {
      // For socket connections, always process them
      if (data.source === 'socket') {
        return true;
      }

      // Check if we've already processed this route in this session
      if (processedPathsInSession.has(data.routeKey)) {
        return false;
      }

      // Add to our set of processed paths for this session
      processedPathsInSession.add(data.routeKey);

      return true;
    }),
    // Process the matched path
    mergeMap(({ matchedPath }) => handleTickerCalls(matchedPath.params, state$.value))
  );
};

export default combineEpics(
  tickerIntializerEpic,
  subscribePositionTickerEpic,
  contractDropdownTickerEpic,
  pollTopGainersLosersEpic,
  tickerEpic
);
